import { useState, useEffect, useRef } from 'react'
import { useLocation, Link } from 'react-router-dom'
import { Globe, ChevronDown, Search, CheckCircle, XCircle, ExternalLink } from 'lucide-react'
import { Card } from '@/components/ui/Card'
import SiluxPromise from '@/components/SiluxPromise'
import SEOHead from '@/components/SEOHead'
import {
  multinetCountries,
  zone1Countries,
  zone2Countries,
  zone3Countries,
  zone4Countries,
  zone5Countries,
  zone6Countries,
  countryZoneMap,
  allRoamingCountries,
  zoneLabels,
  ZoneEntry,
  MultinetEntry,
} from '@/data/roamingData'

const ZONES = ['multinet', 'zone1', 'zone2', 'zone3', 'zone4', 'zone5', 'zone6'] as const
type Zone = typeof ZONES[number]

// Zones shown on the public /roaming page. The World MultiNet zones (zone1-6) are
// intentionally hidden (like the nav and shop filters); only Euro MultiNet is live.
// The zone1-6 data/routes remain so direct links still resolve, just not displayed.
const VISIBLE_ZONES: Zone[] = ['multinet']

const zoneShopPath: Record<string, string> = {
  multinet: '/sim-only/roaming',
  zone1: '/sim-only/roaming',
  zone2: '/sim-only/roaming',
  zone3: '/sim-only/roaming',
  zone4: '/sim-only/roaming',
  zone5: '/sim-only/roaming',
  zone6: '/sim-only/roaming',
}

const zoneData: Record<string, MultinetEntry[] | ZoneEntry[]> = {
  multinet: multinetCountries,
  zone1: zone1Countries,
  zone2: zone2Countries,
  zone3: zone3Countries,
  zone4: zone4Countries,
  zone5: zone5Countries,
  zone6: zone6Countries,
}

const zoneColors: Record<string, string> = {
  multinet: 'from-primary-600 to-cyan-600',
  zone1: 'from-blue-600 to-primary-600',
  zone2: 'from-violet-600 to-blue-600',
  zone3: 'from-emerald-600 to-teal-600',
  zone4: 'from-orange-600 to-amber-600',
  zone5: 'from-rose-600 to-pink-600',
  zone6: 'from-gray-600 to-slate-600',
}

const zoneTabColors: Record<string, string> = {
  multinet: 'border-primary-500 text-primary-600 bg-primary-50',
  zone1: 'border-blue-500 text-blue-600 bg-blue-50',
  zone2: 'border-violet-500 text-violet-600 bg-violet-50',
  zone3: 'border-emerald-500 text-emerald-600 bg-emerald-50',
  zone4: 'border-orange-500 text-orange-600 bg-orange-50',
  zone5: 'border-rose-500 text-rose-600 bg-rose-50',
  zone6: 'border-gray-500 text-gray-600 bg-gray-50',
}

function Tick({ val }: { val: boolean }) {
  return val
    ? <CheckCircle className="w-4 h-4 text-green-500 mx-auto" />
    : <XCircle className="w-4 h-4 text-gray-300 mx-auto" />
}

export default function Roaming() {
  const location = useLocation()
  const [activeZone, setActiveZone] = useState<Zone>('multinet')
  const [countrySearch, setCountrySearch] = useState('')
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Set active zone from URL path or hash
  useEffect(() => {
    const path = location.pathname
    const hash = location.hash.replace('#', '') as Zone
    if (hash && ZONES.includes(hash)) {
      setActiveZone(hash)
    } else if (path.includes('/zone1')) setActiveZone('zone1')
    else if (path.includes('/zone2')) setActiveZone('zone2')
    else if (path.includes('/zone3')) setActiveZone('zone3')
    else if (path.includes('/zone4')) setActiveZone('zone4')
    else if (path.includes('/zone5')) setActiveZone('zone5')
    else if (path.includes('/zone6')) setActiveZone('zone6')
    else if (path.includes('/multinet')) setActiveZone('multinet')
  }, [location])

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const filteredCountries = allRoamingCountries.filter((c) =>
    c.toLowerCase().includes(countrySearch.toLowerCase())
  )

  const selectedCountryZones = selectedCountry ? (countryZoneMap[selectedCountry] || []) : []

  const entries = zoneData[activeZone]
  const isMultinet = activeZone === 'multinet'

  // Group entries by country for display
  const groupedEntries = entries.reduce((acc: Record<string, typeof entries>, entry) => {
    const c = entry.country
    if (!acc[c]) acc[c] = []
    acc[c].push(entry as any)
    return acc
  }, {})

  // Drop a trailing "Sim" from the zone label so the title doesn't read
  // "... Sim Roaming SIM" (e.g. "Euro MultiNet Sim" -> "Euro MultiNet").
  const zoneSeoName = (zoneLabels[activeZone] || 'International').replace(/\s*sim$/i, '')

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-primary-50/30">
      {/* SEO meta ONLY for the visible Euro MultiNet page. The World MultiNet zone
          pages (/roaming/zoneN) are intentionally HIDDEN (not in nav/sitemap) and are
          noindex'd via RouteSEO — do NOT emit indexable meta for them here. The bare
          /roaming page is managed by the admin page_seo system (via RouteSEO). */}
      {location.pathname === '/roaming/multinet' && (
        <SEOHead
          title={`${zoneSeoName} Roaming Data SIM | Silux Mobile`}
          description={`${zoneSeoName} roaming data SIM from Silux Mobile — stay connected abroad with coverage across multiple countries. Check included destinations and rates.`}
          canonical="https://www.siluxmobile.co.uk/roaming/multinet"
        />
      )}
      {/* Hero */}
      <div className="relative overflow-hidden bg-gradient-to-br from-primary-700 via-primary-600 to-cyan-600 text-white">
        <div className="absolute inset-0 bg-grid-white/10" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <div className="text-center">
            <div className="inline-flex p-4 bg-white/20 backdrop-blur-sm rounded-2xl mb-6">
              <Globe className="w-12 h-12 sm:w-16 sm:h-16" />
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold mb-4">International Roaming SIMs</h1>
            <p className="text-xl text-primary-100 mb-3 max-w-3xl mx-auto">
              Stay connected worldwide with our data SIMs covering 180+ countries across 7 zones.
            </p>
            <p className="text-sm text-primary-200 max-w-2xl mx-auto">
              Some Zones offer the same countries but on different networks in their respective countries.
            </p>
          </div>
        </div>
      </div>

      <SiluxPromise variant="sim" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">

        {/* Zone Cards / Filter Icons — only shown when more than one zone is visible.
            With World MultiNet zones hidden, only Euro MultiNet remains, so this whole
            selector is hidden and the page shows MultiNet content directly. */}
        {VISIBLE_ZONES.length > 1 && (
        <div className="mb-10">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Choose Your Roaming Zone</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-4 lg:grid-cols-7 gap-3">
            {VISIBLE_ZONES.map((zone) => {
              const count = Object.keys(
                (zoneData[zone] as any[]).reduce((a: Record<string,boolean>, e: any) => { a[e.country] = true; return a }, {})
              ).length
              return (
                <button
                  key={zone}
                  onClick={() => setActiveZone(zone)}
                  id={zone}
                  className={`flex flex-col items-center p-3 rounded-xl border-2 transition-all hover:shadow-md ${
                    activeZone === zone
                      ? zoneTabColors[zone] + ' shadow-md'
                      : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                  }`}
                >
                  <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${zoneColors[zone]} flex items-center justify-center mb-2`}>
                    <Globe className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-xs font-semibold text-center leading-tight">
                    {zone === 'multinet' ? 'MultiNet Euro Plus' : `Zone ${zone.replace('zone', '')}`}
                  </span>
                  <span className="text-xs text-gray-400 mt-0.5">{count} countries</span>
                </button>
              )
            })}
          </div>
        </div>
        )}

        {/* Country Lookup */}
        <Card className="mb-10 bg-gradient-to-br from-gray-50 to-gray-100 border-gray-200">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Search className="w-5 h-5 text-primary-600" />
            Find Which Zones Cover a Country
          </h2>
          <div className="relative max-w-md" ref={dropdownRef}>
            <div
              className="flex items-center border-2 border-gray-200 rounded-xl bg-white px-4 py-3 cursor-pointer hover:border-primary-400 transition-colors"
              onClick={() => setDropdownOpen(!dropdownOpen)}
            >
              <Search className="w-5 h-5 text-gray-400 mr-3 shrink-0" />
              <input
                type="text"
                className="flex-1 outline-none bg-transparent text-gray-800 placeholder-gray-400"
                placeholder="Search for a country..."
                value={selectedCountry || countrySearch}
                onChange={(e) => {
                  setCountrySearch(e.target.value)
                  setSelectedCountry(null)
                  setDropdownOpen(true)
                }}
                onClick={(e) => { e.stopPropagation(); setDropdownOpen(true) }}
              />
              <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
            </div>
            {dropdownOpen && (
              <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-xl max-h-60 overflow-y-auto">
                {filteredCountries.length === 0 ? (
                  <div className="px-4 py-3 text-gray-500 text-sm">No countries found</div>
                ) : (
                  filteredCountries.map((country) => (
                    <button
                      key={country}
                      className="w-full text-left px-4 py-2.5 hover:bg-primary-50 text-sm text-gray-800 flex items-center justify-between"
                      onClick={() => {
                        setSelectedCountry(country)
                        setCountrySearch('')
                        setDropdownOpen(false)
                      }}
                    >
                      <span>{country}</span>
                      <span className="text-xs text-gray-400">
                        {(countryZoneMap[country] || []).length} zone{(countryZoneMap[country] || []).length !== 1 ? 's' : ''}
                      </span>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          {selectedCountry && (
            <div className="mt-4 p-4 bg-white rounded-xl border border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-3">
                Zones covering <span className="text-primary-600">{selectedCountry}</span>:
              </h3>
              {selectedCountryZones.length === 0 ? (
                <p className="text-gray-500 text-sm">Not covered in any zone</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {selectedCountryZones.map((zone) => (
                    <div key={zone} className="flex items-center gap-2">
                      <button
                        onClick={() => setActiveZone(zone as Zone)}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium bg-gradient-to-r ${zoneColors[zone]} text-white hover:opacity-90 transition-opacity`}
                      >
                        {zone === 'multinet' ? 'MultiNet Euro Plus' : `Zone ${zone.replace('zone', '')}`}
                      </button>
                      <Link
                        to={zoneShopPath[zone]}
                        className="text-xs text-primary-600 hover:underline flex items-center gap-1"
                      >
                        View products <ExternalLink className="w-3 h-3" />
                      </Link>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </Card>

        {/* Country Table */}
        <div>
          <div className={`flex items-center justify-between mb-4`}>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{zoneLabels[activeZone]}</h2>
              <p className="text-gray-500 text-sm mt-1">
                {Object.keys(groupedEntries).length} countries · {entries.length} networks
              </p>
            </div>
            <Link
              to={zoneShopPath[activeZone]}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-white text-sm font-semibold bg-gradient-to-r ${zoneColors[activeZone]} hover:opacity-90 transition-opacity shadow-md`}
            >
              View {activeZone === 'multinet' ? 'MultiNet' : `Zone ${activeZone.replace('zone', '')}`} Products
              <ExternalLink className="w-4 h-4" />
            </Link>
          </div>

          <Card className="overflow-hidden p-0">
            <div className="overflow-x-auto">
              <p className="text-xs text-gray-400 px-4 pt-2 pb-0 sm:hidden flex items-center gap-1">
                ← Scroll to see all columns →
              </p>
              <table className="w-full text-sm min-w-[480px]">
                <thead>
                  <tr className={`bg-gradient-to-r ${zoneColors[activeZone]} text-white`}>
                    <th className="text-left px-4 py-3 font-semibold">Country</th>
                    <th className="text-left px-4 py-3 font-semibold">Network / Operator</th>
                    {isMultinet ? (
                      <>
                        <th className="text-center px-3 py-3 font-semibold">3G</th>
                        <th className="text-center px-3 py-3 font-semibold">LTE</th>
                        <th className="text-center px-3 py-3 font-semibold">5G</th>
                      </>
                    ) : (
                      <>
                        <th className="text-center px-3 py-3 font-semibold">2G</th>
                        <th className="text-center px-3 py-3 font-semibold">3G</th>
                        <th className="text-center px-3 py-3 font-semibold">LTE</th>
                        <th className="text-center px-3 py-3 font-semibold">5G</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(groupedEntries).map(([country, countryEntries], ci) => (
                    countryEntries.map((entry: any, ei: number) => (
                      <tr
                        key={`${country}-${ei}`}
                        className={`border-b border-gray-100 ${ci % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'} hover:bg-primary-50/30 transition-colors`}
                      >
                        {ei === 0 ? (
                          <td
                            rowSpan={countryEntries.length}
                            className="px-4 py-2.5 font-medium text-gray-900 align-top border-r border-gray-100"
                          >
                            {country}
                          </td>
                        ) : null}
                        <td className="px-4 py-2.5 text-gray-600">
                          {isMultinet ? (entry as MultinetEntry).operator : (entry as ZoneEntry).network}
                        </td>
                        {isMultinet ? (
                          <>
                            <td className="px-3 py-2.5 text-center"><Tick val={(entry as MultinetEntry).g3} /></td>
                            <td className="px-3 py-2.5 text-center"><Tick val={(entry as MultinetEntry).lte} /></td>
                            <td className="px-3 py-2.5 text-center"><Tick val={(entry as MultinetEntry).g5} /></td>
                          </>
                        ) : (
                          <>
                            <td className="px-3 py-2.5 text-center"><Tick val={(entry as ZoneEntry).g2} /></td>
                            <td className="px-3 py-2.5 text-center"><Tick val={(entry as ZoneEntry).g3} /></td>
                            <td className="px-3 py-2.5 text-center"><Tick val={(entry as ZoneEntry).lte} /></td>
                            <td className="px-3 py-2.5 text-center"><Tick val={(entry as ZoneEntry).g5} /></td>
                          </>
                        )}
                      </tr>
                    ))
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          <p className="text-xs text-gray-500 mt-3 text-center">
            * Coverage data based on partner network agreements. Actual availability may vary by location.
            Some Zones offer the same countries but on different networks in their respective countries.
          </p>
        </div>
      </div>
    </div>
  )
}
